/**
 * Whether you can stand in your own aura.
 *
 * Four of this pack's auras charge you for being in them — Holy Fire takes 33% of your health
 * plus 33% of your magic shield every half second, Sanguine and Abyss and Plague take their own
 * cuts — and the question that decides whether the build works is not "how big is the hit" but
 * "does anything refill it faster than that". The two halves were on different tabs and neither
 * subtracted the other.
 *
 * ## The order the game spends in, and why it decides the answer
 *
 * `MagicShield` absorbs before health does, so a self-hit lands on the shield first and only the
 * overflow reaches the pool underneath. That makes the two regenerations *sequential* rather
 * than pooled: magic shield regeneration pays first, and health regeneration only pays what the
 * shield's could not.
 *
 * It matters because the two are rarely alike. A build with 40 magic shield a second and 10
 * health a second survives a 35/s drain on the shield alone and never touches its health; the
 * same build against a 60/s drain is losing 20 health a second, not 10 — the shield is already
 * empty and everything past it is arriving on the pool that regenerates slowest. Adding the two
 * regenerations together would call both builds fine.
 *
 * ## Deliberately not netted off anything
 *
 * This answers survival, not damage. Nothing here is subtracted from DPS, and the damage the
 * aura deals is not subtracted from this: what you deal and what you pay are different questions
 * and a single number mixing them would answer neither.
 */
export function selfSustain(input) {
    const drain = Math.max(0, input.perSecond);
    const shieldRegen = Math.max(0, input.magicShield.perSecond);
    const healthRegen = Math.max(0, input.health.perSecond);
    // The shield pays first because the shield is hit first, and it can only pay while there is a
    // shield to regenerate: a build with no magic shield has nothing here however much
    // `magic_shield_regen` its gear rolls, since the stat regenerates a pool of zero.
    const fromShieldRegen = input.magicShield.max > 0 ? Math.min(drain, shieldRegen) : 0;
    const reachingHealth = drain - fromShieldRegen;
    const fromHealthRegen = Math.min(reachingHealth, healthRegen);
    const netLossPerSecond = Math.max(0, reachingHealth - fromHealthRegen);
    const pool = Math.max(0, input.magicShield.max) + Math.max(0, input.health.max);
    const secondsToDeath = netLossPerSecond > 0 ? pool / netLossPerSecond : Infinity;
    // `check_combined_hp_and_ms` — the gate reads both pools as one, so the distance to the cutoff
    // is the share of the *combined* bar above it rather than of health alone.
    const cutoffPool = input.cutoffShare === undefined ? undefined : pool * (1 - input.cutoffShare);
    const secondsToCutoff = cutoffPool === undefined || netLossPerSecond <= 0 ? undefined : cutoffPool / netLossPerSecond;
    return {
        drainPerSecond: drain,
        fromShieldRegen,
        reachingHealth,
        fromHealthRegen,
        netLossPerSecond,
        sustainable: netLossPerSecond <= 0,
        secondsToDeath,
        ...(secondsToCutoff === undefined ? {} : { secondsToCutoff }),
        castsToEmpty: input.perCast > 0 ? pool / input.perCast : Infinity,
    };
}
//# sourceMappingURL=self-sustain.js.map